import { api } from "./api";
import type { ReasonPayload } from "./reasonCodes";

/**
 * OPENAPI-2 · 开放平台后台接口（契约 §7：/api/v1/b2b/open-api/* 与 /api/v1/b2b/partners/{id}/open-api）。
 * OPENAPI-1 后端并行做；样稿灰条没写全的响应字段标「待核」。
 */
export type ApplicationStatus = "pending" | "awaiting_partner_confirm" | "approved" | "rejected";

export interface OpenApiApplication {
  id: string;
  submitted_at: string;
  company_name: string;
  website: string;
  country: string;
  contact_name: string;
  contact_email: string;
  tech_email: string;
  phone?: string;
  channels?: string | null;
  monthly_volume: string | null;
  settlement_wish: string | null;
  remark?: string | null;
  terms_version: string;
  terms_accepted_at?: string | null;
  status: ApplicationStatus;
  decided_at?: string | null;
  decided_by_name?: string | null;
  reject_reason?: string | null;
  /** 系统按公司名 / 邮箱域名比对出的疑似已有同行档案 */
  matched_partner: { id: string; code: string; name: string } | null;
  partner_id?: string | null;
  /** FIX-1 安全 #8 · 申请人邮箱域名与匹配到的档案不一致时的冒名提示（只提示不拦）；没有 = null */
  match_warning?: string | null;
  /** status = awaiting_partner_confirm 时：该同行主账号确认的截止时间（过期后可「重发确认」） */
  confirm_expires_at?: string | null;
}

export interface ApproveBody {
  /** 绑到已有档案（不传 = 新建同行档案） */
  partner_id?: string;
  settlement_mode: "prepay" | "credit_monthly" | "credit_per_trip";
  credit_limit_cents: number;
  /** 新建同行必选；绑到已有同行时不带（后端不改它原来的客群） */
  tier_code?: string;
}

export interface GoLiveRequest {
  id: string;
  partner_id: string;
  partner_code: string;
  partner_name: string;
  requested_at: string;
  checklist_done: number;
  checklist_total: number;
  calls: number;
  sandbox_bookings: number;
  webhook_success_pct: number | null;
  status?: "pending" | "issued";
}

export interface OpenApiKeyRow { id: string; env: "test" | "live"; prefix: string; last4: string; status: "active" | "revoked"; last_used_at: string | null }
export interface PartnerOpenApi {
  stage: "none" | "testing" | "go_live_requested" | "live";
  application: OpenApiApplication | null;
  keys: OpenApiKeyRow[];
  checklist_done: number;
  checklist_total: number;
  /** FIX-3 · 运维信息（只读）：清单第一次全过的时间 / 申请上线时间 / 被退回的原因 / 对方设的 IP 白名单 / 技术联系人 */
  checklist_passed_at?: string | null;
  go_live_requested_at?: string | null;
  go_live_rejection?: { at: string; reason: string } | null;
  ip_allowlist?: string[];
  tech_email?: string | null;
}
type Env = "test" | "live";
export interface WebhookEndpointRow {
  env: Env; url: string; events: string[]; secret_preview: string;
  /** 连续失败触发熔断后暂停到几点（没暂停 = null） */
  paused_until: string | null; consecutive_failures: number;
  disabled: boolean; disabled_at: string | null; disabled_reason: string | null;
}
export interface AdminDelivery {
  id: string; created_at: string; event: string; ref: string | null; http_status: number | null; attempts: number; max_attempts: number;
  next_retry_at: string | null; payload?: unknown; status: string; last_error: string | null;
}
export interface AdminCallLog { request_id: string; at: string; env: Env; method: string; path: string; status: number; error_code: string | null; duration_ms: number; client_ip: string | null }
export interface AdminCallLogDetail extends AdminCallLog { query: string | null; request: unknown; response: unknown }
interface PageOf<T> { items: T[]; total: number; page: number; per_page: number }
export interface PartnerOpenApiStats { calls: number; error_rate_pct: number; bookings_via_api: number; webhook_failed_unrecovered: number }
export interface PendingCounts { applications: number; go_live: number; /** 绑定已有同行、等该同行主账号确认的申请数 */ awaiting_partner_confirm?: number }

const B = "/api/v1/b2b";
export const openApiAdmin = {
  applications: (status: ApplicationStatus) => api<{ items: OpenApiApplication[] }>(`${B}/open-api/applications?status=${status}`),
  approve: (id: string, body: ApproveBody) => api<unknown>(`${B}/open-api/applications/${id}:approve`, { method: "POST", body: JSON.stringify(body) }),
  /** 拒绝须填原因（发给对方）：REASON-1 同款 reason / reason_code */
  reject: (id: string, r: ReasonPayload) => api<unknown>(`${B}/open-api/applications/${id}:reject`, { method: "POST", body: JSON.stringify(r) }),
  /** 「等主账号确认」的申请：重新发确认邮件并把有效期重新算 7 天 */
  resendConfirm: (id: string) => api<unknown>(`${B}/open-api/applications/${id}:resend-confirm`, { method: "POST" }),
  pendingCounts: () => api<PendingCounts>(`${B}/open-api/pending-counts`),
  goLiveRequests: (status: "pending" | "issued" = "pending") => api<{ items: GoLiveRequest[] }>(`${B}/open-api/go-live-requests?status=${status}`),
  partner: (partnerId: string) => api<PartnerOpenApi>(`${B}/partners/${partnerId}/open-api`),
  /** 发正式密钥：后台**不接触明文**（FIX-1 / FIX-3 #16）——只回前缀 + 末 4 位；对方在开放平台「概览 · 密钥」点「领取」取走一次 */
  issueLiveKey: (partnerId: string) => api<{ prefix: string; last4: string; key_id?: string; reveal_hint?: string; /** 通知邮件实际发出去了没有（没邮箱 / 发送失败 = false） */ email_sent?: boolean }>(`${B}/partners/${partnerId}/open-api/live-key:issue`, { method: "POST" }),
  revokeKey: (partnerId: string, keyId: string, r: ReasonPayload) =>
    api<unknown>(`${B}/partners/${partnerId}/open-api/keys/${keyId}:revoke`, { method: "POST", body: JSON.stringify(r) }),
  // ── FIX-3 #9 · 运维入口（同行详情「开放 API」标签）──
  webhookEndpoints: (partnerId: string) => api<{ items: WebhookEndpointRow[] }>(`${B}/partners/${partnerId}/open-api/webhook-endpoints`),
  webhookDeliveries: (partnerId: string, q: { env: Env; event?: string; result?: string; page?: number }) => {
    const u = new URLSearchParams({ env: q.env, page: String(q.page ?? 1) });
    if (q.event) u.set("event", q.event);
    if (q.result) u.set("result", q.result);
    return api<PageOf<AdminDelivery>>(`${B}/partners/${partnerId}/open-api/webhook-deliveries?${u}`);
  },
  /** 停用对方的推送地址：原因必填（场景 openapi_webhook_disable），对方在门户改不回来 */
  disableWebhook: (partnerId: string, env: Env, r: ReasonPayload) =>
    api<WebhookEndpointRow>(`${B}/partners/${partnerId}/open-api/webhooks/${env}:disable`, { method: "POST", body: JSON.stringify(r) }),
  enableWebhook: (partnerId: string, env: Env) => api<WebhookEndpointRow>(`${B}/partners/${partnerId}/open-api/webhooks/${env}:enable`, { method: "POST" }),
  callLogs: (partnerId: string, q: { request_id?: string; env?: Env; errors_only?: boolean; page?: number }) => {
    const u = new URLSearchParams({ page: String(q.page ?? 1) });
    if (q.request_id) u.set("request_id", q.request_id);
    if (q.env) u.set("env", q.env);
    if (q.errors_only) u.set("errors_only", "true");
    return api<PageOf<AdminCallLog>>(`${B}/partners/${partnerId}/open-api/call-logs?${u}`);
  },
  callLog: (partnerId: string, requestId: string) => api<AdminCallLogDetail>(`${B}/partners/${partnerId}/open-api/call-logs/${encodeURIComponent(requestId)}`),
  ipAllowlist: (partnerId: string) => api<{ ip_allowlist: string[] }>(`${B}/partners/${partnerId}/open-api/ip-allowlist`),
  /** 退回上线申请：原因必填（场景 openapi_go_live_reject），同行回「测试中」，原因显示在对方门户并邮件通知 */
  rejectGoLive: (partnerId: string, r: ReasonPayload) =>
    api<{ stage: string; reject_reason: string }>(`${B}/partners/${partnerId}/open-api/go-live:reject`, { method: "POST", body: JSON.stringify(r) }),
  /** 替对方重发测试密钥：旧的立即失效；后台不接触明文，对方在门户「领取」 */
  reissueTestKey: (partnerId: string) =>
    api<{ prefix: string; last4: string; key_id: string; reveal_hint?: string }>(`${B}/partners/${partnerId}/open-api/test-key:reissue`, { method: "POST" }),
  stats: (partnerId: string, days = 30) => api<PartnerOpenApiStats>(`${B}/partners/${partnerId}/open-api/stats?days=${days}`),
};
